/**
 * @file address.ts
 * @author Rapoo Device SDK Team
 * @date 2026-07-21
 * @description Telink 键盘 EEPROM 地址映射表
 *
 * Telink 键盘灯光协议与 CH585M 基本一致，地址生成逻辑复用 shared 版本。
 * 各芯片常量为 Telink 独有，互不耦合。
 *
 * 侧灯/氛围灯/充电灯地址布局（Telink 独有）：
 * +0x0084 ~ +0x0087: 使能寄存器（4字节）
 *   - byte0: 充电灯开关使能（0=常开, 1=跟随氛围灯开关）
 *   - byte1: 氛围灯开关（0=关, 1=开）
 *   - byte2: 侧灯模式（0=单色常亮, 1=RGB轮询, 2=单色呼吸）
 *   - byte3: 侧灯开关（0=关, 1=开）
 * +0x0088 ~ +0x008F: 充电灯参数（8字节，同动态灯效格式）
 * +0x0090 ~ +0x0097: 氛围灯参数（8字节，同动态灯效格式）
 * +0x0098 ~ +0x009F: 侧灯1参数（8字节，同动态灯效格式）
 * +0x00A0 ~ +0x00A7: 侧灯2参数
 * +0x00A8 ~ +0x00AF: 侧灯3参数
 * +0x00B0 ~ +0x00B7: 侧灯4参数
 */

import { ProtocolAddress } from '../../../types/protocol'
import {
  genrateLevelAddress,
  genrateStaticLightEffectPresetAddressName,
  genrateStaticLightEffectPresetAddressLevel,
  genrateStaticLightEffectAddressLevel,
  genrateStaticLightRGBAddressLevel,
  genrateDynamicLightEffectAddressLevel,
  genrateSideLightEffectAddressLevel,
  genrateWindowsBaseLayerAddress,
  genrateMacroAddressLevel,
  genrateMacroNameAddressLevel,
} from './AddressGenerators'

// ==================== 地址常量 ====================
// 4 个配置层的基础地址
const ADDRESS_LEVEL = [0x10002000, 0x10007000, 0x1000c000, 0x10011000]

// 用户自定义静态灯效模式参数存储区
const STATIC_LIGHT_EFFECT_PRESET_ADDRESS = 0x10016000

// 用户自定义静态灯效预设参数表（每个预设的偏移）
const STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL = [
  0x0010, 0x0030, 0x0050, 0x0070, 0x0090, 0x00b0, 0x00d0, 0x00f0, 0x0110, 0x0130, 0x0150, 0x0170,
  0x0190, 0x01b0, 0x01d0,
]

// 用户自定义静态模式灯效 RGB 存储区
const STATIC_LIGHT_EFFECT_ADDRESS_LEVEL = [
  0x1001a000, 0x1001a400, 0x1001a800, 0x1001ac00, 0x1001b000, 0x1001b400, 0x1001b800, 0x1001bc00,
  0x1001c000, 0x1001c400, 0x1001c800, 0x1001cc00, 0x1001d000, 0x1001d400, 0x1001d800,
]

/**
 * 动态模式参数偏移（相对于配置层基础地址），共 16 个模式，每个 8 字节。
 *
 * 这张表**不是连续的**：模式 1~10 排在 +0x0034 ~ +0x0083，之后被辅助灯区
 * （使能寄存器 +0x0084、充电灯 +0x0088、氛围灯 +0x0090、侧灯 1~4 +0x0098 ~ +0x00B7）
 * 和消抖时间设置（+0x00B8 ~ +0x00BB）截断，模式 11~16 从 +0x00BC 继续排到 +0x00EB。
 *
 * 数组下标即模式序号 - 1，与业务侧灯效 id 的换算是 `id - 100`（见 dynamicDomain.normalizeIndex）。
 * 千万不要按 8 字节步长外推补齐——+0x0084 起是辅助灯区，写进去会改坏侧灯/氛围灯配置。
 */
const DYNAMIC_LIGHT_EFFECT_ADDRESS_LEVEL = [
  // 模式 1~8：+0x0034 ~ +0x0073
  0x0034, 0x003c, 0x0044, 0x004c, 0x0054, 0x005c, 0x0064, 0x006c,
  // 模式 9~10：+0x0074 ~ +0x0083
  0x0074, 0x007c,
  // 模式 11~16：+0x00BC ~ +0x00EB（跨过辅助灯区与消抖时间）
  0x00bc, 0x00c4, 0x00cc, 0x00d4, 0x00dc, 0x00e4,
]

// 侧灯参数偏移（4 个侧灯，每个 8 字节）
const SIDE_LIGHT_EFFECT_ADDRESS_LEVEL = [0x0098, 0x00a0, 0x00a8, 0x00b0]

export const ADDRESS_TELINK = {
  // ==================== 基础配置 ====================

  CURRENT_CONNECT_PROTOCOL: [0x2c, 0x00], //TODO 不确定, 似乎没用

  CONFIG_CURRENT: [0x2d, 0x00], //0~3 表示配置 1/2/3/4

  FW_VERSION_0: [0x08, 0x00], //固件版本字节 1（ 最低字节）
  FW_VERSION_1: [0x09, 0x00], //固件版本字节 2
  FW_VERSION_2: [0x0a, 0x00], //固件版本字节 3
  FW_VERSION_3: [0x0b, 0x00], //固件版本字节 4（ 最高字节）

  STATIC_LIGHTING_EFFECT_COUNT: [0x2f, 0x00], //出厂自带静态灯效数量

  SYS_MODE: [0x38, 0x00], // 获取系统模式 0:win 1:mac

  CONFIG_ADDRESS: genrateLevelAddress(ADDRESS_LEVEL, 0x0000), //配置初始地址
  CONFIG_NAME: genrateLevelAddress(ADDRESS_LEVEL, 0x00f0), //配置名称
  FN_LOCK: genrateLevelAddress(ADDRESS_LEVEL, 0x0004), //FnLock: 0: 没锁; 1: 锁定
  WIN_LOCK: genrateLevelAddress(ADDRESS_LEVEL, 0x0005), //WinLock: 0: 没锁; 1: 锁定
  FULL_KEY_LOCK: genrateLevelAddress(ADDRESS_LEVEL, 0x0006), //FullKeyLock: 0: 没锁； 1： 锁定
  BACKLIGHT_SWITCH: genrateLevelAddress(ADDRESS_LEVEL, 0x0007), //背光开关: 0: OFF; 1: ON; 0xff: 不支持

  BACKLIGHT_EFFECT: genrateLevelAddress(ADDRESS_LEVEL, 0x0008), //当前背光灯效模式
  GLOBAL_BRIGHTNESS: genrateLevelAddress(ADDRESS_LEVEL, 0x0009), //全局亮度档位(0~100%)
  GLOBAL_SPEED: genrateLevelAddress(ADDRESS_LEVEL, 0x000a), //全局速度档位(0~100%)

  // 键盘性能参数
  REPORT_RATE: genrateLevelAddress(ADDRESS_LEVEL, 0x002c), //回报率偏移地址: 0x08=125Hz, 0x04=250Hz, 0x02=500Hz, 0x01=1000Hz, 0x84=2K, 0x82=4K, 0x81=8K
  SLEEP_TIME: genrateLevelAddress(ADDRESS_LEVEL, 0x0030), //休眠时间偏移地址: 单位分钟, 可设范围 3~120, 默认 30

  // 动态模式参数按模式序号取址统一走下方的 DYNAMIC_LIGHT_* 表
  // （源数据为 DYNAMIC_LIGHT_EFFECT_ADDRESS_LEVEL，共 16 个模式且地址不连续）

  // 动态模式参数内部偏移
  DYNAMIC_BRIGHTNESS_OFFSET: 0, //亮度偏移
  DYNAMIC_SPEED_OFFSET: 1, //速度偏移
  DYNAMIC_COLOR_TYPE_OFFSET: 2, //颜色类型偏移（0=随机/炫彩, 1=固定颜色）
  DYNAMIC_COLOR_R_OFFSET: 4, //红色分量偏移
  DYNAMIC_COLOR_G_OFFSET: 5, //绿色分量偏移
  DYNAMIC_COLOR_B_OFFSET: 6, //蓝色分量偏移

  // 静态灯效 RGB 地址（6行×22列按键矩阵，每点位 4 字节：R/G/B/保留）
  STATIC_LIGHT_R0C0: genrateStaticLightEffectAddressLevel(
    STATIC_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x0000,
  ),
  STATIC_LIGHT_NAME: genrateStaticLightEffectAddressLevel(
    STATIC_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x0210,
  ),
  STATIC_CUSTOM_LIGHT_RGB: genrateStaticLightRGBAddressLevel(
    STATIC_LIGHT_EFFECT_ADDRESS_LEVEL,
    22,
    6,
    0x00,
  ),

  // 自定义静态灯效名称地址（15 个预设 × 0x20 字节每个）
  STATIC_LIGHT_EFFECT_ADDRESS_NAME_LEVEL: genrateStaticLightEffectPresetAddressName(
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS,
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL,
    0x00,
  ),

  // 背光灯效静态模式参数表
  STATIC_LIGHT_TYPE: genrateStaticLightEffectPresetAddressLevel(
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS,
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL,
    0x00,
  ), //背光灯效静态模式类型: 1=无渐变, 2=上下渐变, 3=左右渐变
  STATIC_LIGHT_BRIGHTNESS: genrateStaticLightEffectPresetAddressLevel(
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS,
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL,
    0x01,
  ),
  STATIC_LIGHT_EFFECT_MODE: genrateStaticLightEffectPresetAddressLevel(
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS,
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL,
    0x02,
  ), //静态灯效 0~4：默认显示 0xff: 显示为对应的 RGB 存储区

  // 渐变颜色（起始-结束）
  STATIC_GRADIENT_COLOR_START_R: genrateStaticLightEffectPresetAddressLevel(
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS,
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL,
    0x02,
  ),
  STATIC_GRADIENT_COLOR_START_G: genrateStaticLightEffectPresetAddressLevel(
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS,
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL,
    0x03,
  ),
  STATIC_GRADIENT_COLOR_START_B: genrateStaticLightEffectPresetAddressLevel(
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS,
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL,
    0x04,
  ),
  STATIC_GRADIENT_COLOR_END_R: genrateStaticLightEffectPresetAddressLevel(
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS,
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL,
    0x05,
  ),
  STATIC_GRADIENT_COLOR_END_G: genrateStaticLightEffectPresetAddressLevel(
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS,
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL,
    0x06,
  ),
  STATIC_GRADIENT_COLOR_END_B: genrateStaticLightEffectPresetAddressLevel(
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS,
    STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL,
    0x07,
  ),

  // 动态灯效参数表
  DYNAMIC_LIGHT_BRIGHTNESS: genrateDynamicLightEffectAddressLevel(
    ADDRESS_LEVEL,
    DYNAMIC_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x00,
  ), //动态灯效亮度
  DYNAMIC_LIGHT_SPEED: genrateDynamicLightEffectAddressLevel(
    ADDRESS_LEVEL,
    DYNAMIC_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x01,
  ), //动态灯效速度
  DYNAMIC_LIGHT_COLOR_TYPE: genrateDynamicLightEffectAddressLevel(
    ADDRESS_LEVEL,
    DYNAMIC_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x02,
  ), //颜色类型: 0=随机/炫彩, 1=固定颜色
  DYNAMIC_LIGHT_COLOR_R: genrateDynamicLightEffectAddressLevel(
    ADDRESS_LEVEL,
    DYNAMIC_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x04,
  ), //红色分量
  DYNAMIC_LIGHT_COLOR_G: genrateDynamicLightEffectAddressLevel(
    ADDRESS_LEVEL,
    DYNAMIC_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x05,
  ), //绿色分量
  DYNAMIC_LIGHT_COLOR_B: genrateDynamicLightEffectAddressLevel(
    ADDRESS_LEVEL,
    DYNAMIC_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x06,
  ), //蓝色分量

  // ==================== 按键映射地址（与 CH585M 共享地址池） ====================
  // windows 基础层
  WIN_KEY_MATRIX: genrateWindowsBaseLayerAddress(0x0000, 'WINDOWS_BASE_LAYER_ADDRESS'),
  // windows Fn 层
  WIN_FN_KEY_MATRIX: genrateWindowsBaseLayerAddress(0x0000, 'WINDOWS_FN_LAYER_ADDRESS'),
  // MAC 基础层
  MAC_KEY_MATRIX: genrateWindowsBaseLayerAddress(0x0000, 'MAC_BASE_LAYER_ADDRESS'),
  // MAC Fn 层
  MAC_FN_KEY_MATRIX: genrateWindowsBaseLayerAddress(0x0000, 'MAC_FN_LAYER_ADDRESS'),

  // ==================== 宏地址（与 CH585M 共享地址池） ====================
  MACRO_PACKAGE_ADDRESS: genrateMacroAddressLevel(0x00),
  MACRO_NAME_ADDRESS: genrateMacroNameAddressLevel(0x00),

  // ==================== 侧灯/氛围灯/充电灯（Telink 独有地址） ====================

  // +0x0084 ~ 0x0087
  // 字节 0：充电灯开关使能（0--常开,1--跟随氛围灯开关）
  // 字节 1：氛围灯开关（0--关，1--开）
  // 字节 2: 侧灯模式（0~3）：0--单色常亮 1--RGB 轮询 2--单色呼吸
  // 字节 3：侧灯开关（0--关，1--开）
  SIDE_LIGHT_ENABLE: genrateLevelAddress(ADDRESS_LEVEL, 0x0084),

  // +0x0088 ~ 0x008F 充电灯参数：参照氛围灯模式参数表
  CHARGE_LIGHT_PARAM: genrateLevelAddress(ADDRESS_LEVEL, 0x0088),

  // +0x0090 ~ 0x0097 氛围灯参数：参照氛围灯模式参数表（RGB 领域）
  AMBIENT_LIGHT_PARAM: genrateLevelAddress(ADDRESS_LEVEL, 0x0090),

  // 侧灯参数（4 个侧灯，每个参照氛围灯模式参数表，8 字节）
  SIDE_LIGHT_EFFECT_ADDRESS_1: genrateLevelAddress(ADDRESS_LEVEL, 0x0098), // +0x0098 ~ 0x009F 侧灯1参数
  SIDE_LIGHT_EFFECT_ADDRESS_2: genrateLevelAddress(ADDRESS_LEVEL, 0x00a0), // +0x00A0 ~ 0x00A7 侧灯2参数
  SIDE_LIGHT_EFFECT_ADDRESS_3: genrateLevelAddress(ADDRESS_LEVEL, 0x00a8), // +0x00A8 ~ 0x00AF 侧灯3参数
  SIDE_LIGHT_EFFECT_ADDRESS_4: genrateLevelAddress(ADDRESS_LEVEL, 0x00b0), // +0x00B0 ~ 0x00B7 侧灯4参数

  // 侧灯动态模式参数表（4 个侧灯 × 偏移）
  SIDE_DYNAMIC_LIGHT_BRIGHTNESS: genrateSideLightEffectAddressLevel(
    ADDRESS_LEVEL,
    SIDE_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x00,
  ), //侧灯亮度
  SIDE_DYNAMIC_LIGHT_SPEED: genrateSideLightEffectAddressLevel(
    ADDRESS_LEVEL,
    SIDE_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x01,
  ), //侧灯速度
  SIDE_DYNAMIC_LIGHT_COLOR_TYPE: genrateSideLightEffectAddressLevel(
    ADDRESS_LEVEL,
    SIDE_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x02,
  ), //颜色类型: 0=随机, 1=固定
  SIDE_DYNAMIC_LIGHT_COLOR_R: genrateSideLightEffectAddressLevel(
    ADDRESS_LEVEL,
    SIDE_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x04,
  ), //红色分量
  SIDE_DYNAMIC_LIGHT_COLOR_G: genrateSideLightEffectAddressLevel(
    ADDRESS_LEVEL,
    SIDE_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x05,
  ), //绿色分量
  SIDE_DYNAMIC_LIGHT_COLOR_B: genrateSideLightEffectAddressLevel(
    ADDRESS_LEVEL,
    SIDE_LIGHT_EFFECT_ADDRESS_LEVEL,
    0x06,
  ), //蓝色分量
} as const satisfies ProtocolAddress
